// ======================================================
// REHBER360 — AKILLI PLAN ROBOTU
// SLOT BUILDER
// ======================================================
//
// Bu katman:
// - Planın toplam çalışma kapasitesini hesaplar
// - Strateji oranlarına göre çalışma bütçesi oluşturur
// - Ders önceliklerini dikkate alır
// - Konu durumlarını dikkate alır
// - Yeni konu / soru / tekrar / deneme / telafi slotları üretir
// - Kaynak önerisi ekleyebilir
//
// Bu katman GÜN ATAMAZ.
//
// Örn:
// {
//   type: "new_topic",
//   subject: "TYT - Matematik",
//   topic: "Üslü Sayılar",
//   duration: 60
// }
//
// Bu görev henüz Pazartesi veya Salı değildir.
// Gün ataması smartPlanScheduler.js tarafından yapılacaktır.
// ======================================================

import {
  getMufredatVerisi,
  kaynaklarHavuzu
} from '../../data/coaching/coachingCatalog';

import {
  getSmartPlanStrategy
} from './smartPlanStrategies';

import {
  SMART_PLAN_TASK_TYPES,
  SMART_PLAN_TOPIC_STATUS,
  SMART_PLAN_PRIORITY_LEVELS,
  getSubjectPriorityWeight,
  canUseTopicAsNew,
  sanitizeSelectedSubjects,
  getWeeklyBufferSlotCount,
  getFullMockFrequencyWeeks,
  getBranchMockFrequencyWeeks,
  validateSmartPlanInput
} from './smartPlanRules';


// ======================================================
// 1. SLOT BUILDER SABİTLERİ
// ======================================================

export const SMART_PLAN_SLOT_DEFAULTS = {

  minSlotMinutes: 20,

  bufferMinutes: 30,

     durations: {
  newTopic: 60,
  practice: 45,
  review: 30,
  mixedPractice: 40,

  // Tek bir çalışılmış konuyu ölçen
  // kısa formative konu taraması.
  //
  // Tek konu için WEEKLY_SCAN ölçeğinde
  // 30 dk ayırmak yerine minimum uygulanabilir
  // assessment bloğunu kullanıyoruz.
  topicScan: 20,

  // Birden fazla çalışılmış konuyu kapsayan
  // formative haftalık tarama.
  weeklyScan: 30,

  branchMock: 40,
  fullMock: 120,
  mockAnalysis: 30
},

  questionCounts: {

    Kolay: {
      newTopic: 15,
      practice: 25,
      review: 15,
      mixedPractice: 25,
      branchMock: 15
    },

    Orta: {
      newTopic: 20,
      practice: 30,
      review: 20,
      mixedPractice: 30,
      branchMock: 20
    },

    Zor: {
      newTopic: 25,
      practice: 35,
      review: 25,
      mixedPractice: 35,
      branchMock: 25
    }
  }
};


// ======================================================
// 2. SLOT ID ÜRETİCİ
// ======================================================
//
// Date.now kullanılmıyor.
//
// Aynı girdiler aynı mantıksal sırada slot üretir.
// Böylece motor deterministik kalır.
// ======================================================

const createSlotId = index => {
  return `smart_slot_${String(index).padStart(4, '0')}`;
};


// ======================================================
// 3. KONU ANAHTARI
// ======================================================

export const createSmartPlanTopicKey = ({
  subject,
  topic
}) => {
  return `${subject}__${topic}`;
};


// ======================================================
// 4. KONU DURUMUNU GETİR
// ======================================================

export const getSmartPlanTopicStatus = ({
  subject,
  topic,
  topicStatuses = {}
}) => {

  const key =
    createSmartPlanTopicKey({
      subject,
      topic
    });

  return (
    topicStatuses[key] ||
    SMART_PLAN_TOPIC_STATUS.NOT_STARTED
  );
};


// ======================================================
// 5. ÇALIŞMA SEVİYESİ → KAYNAK ZORLUĞU
// ======================================================

export const resolveSmartPlanDifficulty = ({
  requestedDifficulty,
  studentLevel,
  strategy
}) => {

  // Kullanıcı özellikle seçmişse onu kullan.
  if (
    ['Kolay', 'Orta', 'Zor'].includes(
      requestedDifficulty
    )
  ) {
    return requestedDifficulty;
  }


  // Strateji doğrudan bir seviye söylüyorsa.
  if (
    ['Kolay', 'Orta', 'Zor'].includes(
      strategy?.preferredDifficulty
    )
  ) {
    return strategy.preferredDifficulty;
  }


  // "Otomatik" ise öğrenci seviyesinden çöz.
  const normalized =
    String(studentLevel || '')
      .trim()
      .toLocaleLowerCase('tr-TR');


  if (
    normalized.includes('başlangıç') ||
    normalized.includes('temel') ||
    normalized.includes('kolay')
  ) {
    return 'Kolay';
  }


  if (
    normalized.includes('ileri') ||
    normalized.includes('zor')
  ) {
    return 'Zor';
  }


  return 'Orta';
};


// ======================================================
// 6. KAYNAK ÖNERİSİ
// ======================================================
//
// Havuzdaki ilk kaynak deterministik olarak seçilir.
//
// Daha sonra kullanıcı kaynak tercih sistemi eklenirse
// bu fonksiyon genişletilebilir.
// ======================================================

export const getSmartPlanResourceSuggestion = ({
  subject,
  difficulty,
  preferredResources
}) => {

  const subjectResources =
    kaynaklarHavuzu?.[subject];

  if (!subjectResources) {
    return null;
  }


  // ----------------------------------------------------
  // KULLANICI KAYNAK TERCİHİ
  // ----------------------------------------------------
  //
  // Kullanıcı bu ders için en az bir kaynak seçmişse
  // robot SADECE bu kaynaklar arasından seçim yapar.
  //
  // Öncelik:
  // 1. İstenen zorluk seviyesindeki seçili kaynak
  // 2. Yoksa diğer seçili kaynaklardan ilk geçerli kaynak
  //
  // Seçilmemiş bir kaynağa fallback YAPILMAZ.
  // ----------------------------------------------------

  const hasExplicitPreferences =
    Array.isArray(
      preferredResources
    ) &&
    preferredResources.length > 0;


  if (hasExplicitPreferences) {

    const requestedDifficultyResources =
      Array.isArray(
        subjectResources?.[
          difficulty
        ]
      )
        ? subjectResources[
            difficulty
          ]
        : [];


    const preferredAtDifficulty =
      preferredResources.find(
        resource =>
          requestedDifficultyResources
            .includes(
              resource
            )
      );


    if (preferredAtDifficulty) {
      return preferredAtDifficulty;
    }


    // İstenen zorlukta seçili kaynak yoksa,
    // yine yalnız kullanıcının seçtikleri içinde kal.
    //
    // Havuzda artık bulunmayan eski/stale bir
    // tercih yanlışlıkla kullanılmasın.

    const allKnownResources =
      new Set(
        Object.values(
          subjectResources
        )
          .flatMap(
            resources =>
              Array.isArray(resources)
                ? resources
                : []
          )
      );


    const firstValidPreferred =
      preferredResources.find(
        resource =>
          allKnownResources.has(
            resource
          )
      );


    return (
      firstValidPreferred ||
      null
    );
  }


  // ----------------------------------------------------
  // LEGACY / OTOMATİK DAVRANIŞ
  // ----------------------------------------------------
  //
  // Kullanıcı kaynak seçmemişse mevcut davranış
  // birebir korunur.
  // ----------------------------------------------------

  const preferred =
    subjectResources?.[
      difficulty
    ];


  if (
    Array.isArray(preferred) &&
    preferred.length > 0
  ) {
    return preferred[0];
  }


  const fallbackOrder = [
    'Orta',
    'Kolay',
    'Zor'
  ];


  for (
    const level of fallbackOrder
  ) {

    const resources =
      subjectResources?.[
        level
      ];


    if (
      Array.isArray(resources) &&
      resources.length > 0
    ) {
      return resources[0];
    }
  }


  return null;
};

// ======================================================
// 7. STRATEJİNİN ETKİLİ ORANLARI
// ======================================================
//
// allowNewTopics=false ise "newTopic" bütçesini
// zorla sıfırlıyoruz.
//
// Kalan oranları tekrar normalize ediyoruz.
//
// Böylece örneğin Son Düzlük stratejisi:
// - Yeni konu üretmez
// - Boşa çıkan kapasiteyi diğer çalışma türlerine dağıtır.
// ======================================================

export const getEffectiveStrategyRatios = strategy => {

  const source = {
    newTopic:
      Number(strategy?.ratios?.newTopic || 0),

    practice:
      Number(strategy?.ratios?.practice || 0),

    review:
      Number(strategy?.ratios?.review || 0),

    assessment:
      Number(strategy?.ratios?.assessment || 0)
  };


  if (
    strategy?.allowNewTopics === false
  ) {
    source.newTopic = 0;
  }


  const total =
    source.newTopic +
    source.practice +
    source.review +
    source.assessment;


  if (total <= 0) {
    return {
      newTopic: 0,
      practice: 0.4,
      review: 0.3,
      assessment: 0.3
    };
  }


  return {
    newTopic:
      source.newTopic / total,

    practice:
      source.practice / total,

    review:
      source.review / total,

    assessment:
      source.assessment / total
  };
};


// ======================================================
// 8. DERSİN ETKİLİ KONU HAVUZU
// ======================================================
//
// Kullanıcı belirli konular seçmişse yalnız onlar.
//
// Seçmemişse dersin müfredatının tamamı.
//
// Bu fonksiyon henüz konu durumuna göre filtrelemez.
// ======================================================

export const getEffectiveTopicPool = ({
  subject,
  selectedTopics = {},
  curriculumModel
}) => {

  const curriculumData =
    getMufredatVerisi(
      curriculumModel
    );


  const explicitTopics =
    selectedTopics?.[subject];


  if (
    Array.isArray(explicitTopics) &&
    explicitTopics.length > 0
  ) {
    return explicitTopics.filter(topic =>
      curriculumData?.[subject]
        ?.includes(topic)
    );
  }


  const curriculum =
    curriculumData?.[subject];


  if (!Array.isArray(curriculum)) {
    return [];
  }


  return [...curriculum];
};

// ======================================================
// 9. YENİ KONU HAVUZU
// ======================================================

export const getNewTopicPool = ({
  subject,
  selectedTopics = {},
  topicStatuses = {},
  prioritizeWeakTopics = false,
  curriculumModel
}) => {

  const pool =
    getEffectiveTopicPool({
      subject,
      selectedTopics,
      curriculumModel
    });


  const filtered =
    pool.filter(topic =>
      canUseTopicAsNew({
        subject,
        topic,
        topicStatuses,
        curriculumModel
      })
    );


  if (!prioritizeWeakTopics) {
    return filtered;
  }


  return [...filtered].sort((a, b) => {

    const statusA =
      getSmartPlanTopicStatus({
        subject,
        topic: a,
        topicStatuses
      });

    const statusB =
      getSmartPlanTopicStatus({
        subject,
        topic: b,
        topicStatuses
      });


    const score = status => {

      if (
        status ===
        SMART_PLAN_TOPIC_STATUS.INCOMPLETE
      ) {
        return 0;
      }

      if (
        status ===
        SMART_PLAN_TOPIC_STATUS.NOT_STARTED
      ) {
        return 1;
      }

      return 2;
    };


    return (
      score(statusA) -
      score(statusB)
    );
  });
};


// ======================================================
// 10. TEKRAR / PRATİK KONU HAVUZU
// ======================================================
//
// Eksik ve tamamlanmış konular,
// tekrar/pratik çalışmalarında öne alınır.
// ======================================================

export const getPracticeTopicPool = ({
  subject,
  selectedTopics = {},
  topicStatuses = {},
  prioritizeWeakTopics = false,
  curriculumModel
}) => {

  const pool =
    getEffectiveTopicPool({
      subject,
      selectedTopics,
      curriculumModel
    });


  if (!prioritizeWeakTopics) {
    return pool;
  }


  return [...pool].sort((a, b) => {

    const statusA =
      getSmartPlanTopicStatus({
        subject,
        topic: a,
        topicStatuses
      });

    const statusB =
      getSmartPlanTopicStatus({
        subject,
        topic: b,
        topicStatuses
      });


    const score = status => {

      if (
        status ===
        SMART_PLAN_TOPIC_STATUS.INCOMPLETE
      ) {
        return 0;
      }

      if (
        status ===
        SMART_PLAN_TOPIC_STATUS.COMPLETED
      ) {
        return 1;
      }

      return 2;
    };


    return (
      score(statusA) -
      score(statusB)
    );
  });
};


// ======================================================
// 11. DERS AĞIRLIKLARI
// ======================================================

export const buildSubjectWeights = ({
  selectedSubjects = [],
  subjectPriorities = {}
}) => {

  return selectedSubjects.map(subject => {

    const priority =
      subjectPriorities?.[subject] ||
      SMART_PLAN_PRIORITY_LEVELS.NORMAL;


    return {
      subject,
      priority,
      weight:
        getSubjectPriorityWeight(
          priority
        )
    };
  });
};


// ======================================================
// 12. TOPLAM ÇALIŞMA KAPASİTESİ
// ======================================================
//
// workloadMultiplier:
//
// Başlangıç = 0.8 gibi değerlerde kapasite azaltılabilir.
//
// 1'in üzerindeki katsayıların HARD LIMIT'i aşmasına
// izin verilmez.
//
// Örn:
// 120 dk x 5 gün x 4 hafta = 2400 dk kapasite.
//
// Son Düzlük 1.05 olsa bile 2520 yapılmaz.
// HARD LIMIT 2400'dür.
// ======================================================

export const calculateSmartPlanCapacity = ({
  weeks,
  workingDays = [],
  dailyMinutes,
  strategy
}) => {

  const rawCapacity =
    Number(weeks || 0) *
    Number(workingDays.length || 0) *
    Number(dailyMinutes || 0);


  const workloadMultiplier =
    Math.max(
      0,
      Number(
        strategy?.workloadMultiplier || 1
      )
    );


  const effectiveMultiplier =
    Math.min(
      1,
      workloadMultiplier
    );


  const plannedCapacity =
    Math.floor(
      rawCapacity *
      effectiveMultiplier
    );


  return {
    rawCapacity,
    plannedCapacity,
    workloadMultiplier
  };
};


// ======================================================
// 13. BUFFER KAPASİTESİ
// ======================================================

export const calculateBufferBudget = ({
  weeks,
  strategyId,
  plannedCapacity
}) => {

  const weeklyBufferCount =
    getWeeklyBufferSlotCount(
      strategyId
    );


  const requestedCount =
    Number(weeks || 0) *
    weeklyBufferCount;


  const requestedMinutes =
    requestedCount *
    SMART_PLAN_SLOT_DEFAULTS
      .bufferMinutes;


  // Buffer toplam planın %20'sini geçmesin.
  const maxBufferMinutes =
    Math.floor(
      Number(plannedCapacity || 0) *
      0.20
    );


  const minutes =
    Math.min(
      requestedMinutes,
      maxBufferMinutes
    );


  const count =
    Math.floor(
      minutes /
      SMART_PLAN_SLOT_DEFAULTS
        .bufferMinutes
    );


  return {
    count,
    minutes
  };
};


// ======================================================
// 14. ÇALIŞMA TÜRÜ BÜTÇELERİ
// ======================================================

export const calculateTypeBudgets = ({
  usableMinutes,
  ratios
}) => {

  return {
    newTopic:
      Math.floor(
        usableMinutes *
        ratios.newTopic
      ),

    practice:
      Math.floor(
        usableMinutes *
        ratios.practice
      ),

    review:
      Math.floor(
        usableMinutes *
        ratios.review
      ),

    assessment:
      Math.floor(
        usableMinutes *
        ratios.assessment
      )
  };
};


// ======================================================
// 15. SLOT SÜRESİNİ HESAPLA
// ======================================================

const resolveSlotDuration = ({
  preferredDuration,
  remainingMinutes
}) => {

  if (
    remainingMinutes <
    preferredDuration
  ) {
    return 0;
  }


  return preferredDuration;
};


// ======================================================
// 16. SORU SAYISI
// ======================================================

const getQuestionCount = ({
  difficulty,
  type
}) => {

  const difficultyConfig =
    SMART_PLAN_SLOT_DEFAULTS
      .questionCounts?.[difficulty] ||
    SMART_PLAN_SLOT_DEFAULTS
      .questionCounts.Orta;


  if (
    type ===
    SMART_PLAN_TASK_TYPES.NEW_TOPIC
  ) {
    return difficultyConfig.newTopic;
  }


  if (
    type ===
    SMART_PLAN_TASK_TYPES.PRACTICE
  ) {
    return difficultyConfig.practice;
  }


  if (
    type ===
    SMART_PLAN_TASK_TYPES.REVIEW
  ) {
    return difficultyConfig.review;
  }


  if (
    type ===
    SMART_PLAN_TASK_TYPES.MIXED_PRACTICE
  ) {
    return difficultyConfig.mixedPractice;
  }


  if (
    type ===
    SMART_PLAN_TASK_TYPES.BRANCH_MOCK
  ) {
    return difficultyConfig.branchMock;
  }


  return null;
};


// ======================================================
// 17. SLOT NESNESİ
// ======================================================

const createStudySlot = ({
  index,
  type,
  subject = null,
  topic = null,
  duration,
  difficulty = null,
  resource = null,
  questionCount = null,
  priority = null,
  priorityWeight = 1,
  topicStatus = null,
  reason = null,
  metadata = {}
}) => {

  return {
    id:
      createSlotId(index),

    type,

    subject,

    topic,

    duration,

    difficulty,

    resource,

    questionCount,

    priority,

    priorityWeight,

    topicStatus,

    reason,

    scheduled: false,

    ...metadata
  };
};


// ======================================================
// 18. DERS BAZLI SLOT ÜRETİCİ
// ======================================================

const buildSubjectStudySlots = ({
  type,
  totalBudget,
  subjects,
  selectedTopics,
  topicStatuses,
  difficulty,
  strategy,
  weeks,
  curriculumModel,

  resourcePreferences = {},

  allowedNotStartedTopicKeys = null,
  allowedNewTopicKeys = null,
  guaranteeFullNewTopicCoverage = false,
  guaranteeFirstPracticeCoverage = false,
  startingIndex
}) => {

  const slots = [];

  let slotIndex =
    startingIndex;

  let usedMinutes = 0;


  const totalWeight =
    subjects.reduce(
      (sum, item) =>
        sum + item.weight,
      0
    );


  if (
  totalWeight <= 0 ||
  totalBudget <= 0
) {
  return {
    slots,
    usedMinutes,
    nextIndex: slotIndex
  };
}


// ----------------------------------------------------
// ZORUNLU İLK PRACTICE KATMANI
// ----------------------------------------------------
//
// Quick Dengeli planda:
//
// NEW_TOPIC olarak açılmasına izin verilen her
// NOT_STARTED konu için en az 1 PRACTICE:
//
// 45 dk
//
// ayrılabilsin.
//
// Bu güvence:
//
// - toplam PRACTICE bütçesi gerçekten yeterliyse
//   aktif olur,
// - ders ağırlıklarının minimum öğrenme zincirini
//   kesmesini engeller,
// - kalan bütçeyi ağırlıklı dağıtıma bırakır.
//
// Örnek:
//
// Türkçe    → 2 konu = 90 dk minimum
// Matematik → 1 konu = 45 dk minimum
// Geometri  → 1 konu = 45 dk minimum
//
// Toplam zorunlu = 180 dk.
//
// Eğer totalBudget >= 180:
//
// önce bu 180 dk garanti edilir,
// kalan kapasite ders ağırlıklarıyla dağıtılır.
// ----------------------------------------------------

const firstPracticeGuaranteeRequested =
  guaranteeFirstPracticeCoverage === true &&

  type ===
    SMART_PLAN_TASK_TYPES.PRACTICE &&

  allowedNotStartedTopicKeys
    instanceof Set;


let guaranteedFirstPracticeMinutes =
  0;


if (
  firstPracticeGuaranteeRequested
) {

  subjects.forEach(
    subjectInfo => {

      const subject =
        subjectInfo.subject;


     const practicePool =
  getPracticeTopicPool({
    subject,

    selectedTopics,

    topicStatuses,

    prioritizeWeakTopics:
      strategy?.prioritizeWeakTopics,

    curriculumModel
  });


      practicePool.forEach(
        topic => {

          const status =
            getSmartPlanTopicStatus({
              subject,
              topic,
              topicStatuses
            });


          if (
            status !==
              SMART_PLAN_TOPIC_STATUS
                .NOT_STARTED
          ) {
            return;
          }


          const topicKey =
            createSmartPlanTopicKey({
              subject,
              topic
            });


          if (
            !allowedNotStartedTopicKeys
              .has(topicKey)
          ) {
            return;
          }


          guaranteedFirstPracticeMinutes +=
            SMART_PLAN_SLOT_DEFAULTS
              .durations.practice;
        }
      );
    }
  );
}


const firstPracticeGuaranteeActive =
  firstPracticeGuaranteeRequested &&

  guaranteedFirstPracticeMinutes >
    0 &&

  guaranteedFirstPracticeMinutes <=
    Number(
      totalBudget || 0
    );


const weightedRemainderBudget =
  firstPracticeGuaranteeActive
    ? Math.max(
        0,

        Number(
          totalBudget || 0
        ) -
        guaranteedFirstPracticeMinutes
      )
    : Number(
        totalBudget || 0
      );


subjects.forEach(subjectInfo => {

    const {
      subject,
      priority,
      weight
    } = subjectInfo;


  const weightedSubjectBudget =
  Math.floor(
    weightedRemainderBudget *
    (weight / totalWeight)
  );


let topicPool = [];


let mandatoryFirstPracticeMinutesForSubject =
  0;


if (
  type ===
  SMART_PLAN_TASK_TYPES.NEW_TOPIC
) {

topicPool =
  getNewTopicPool({
    subject,
    selectedTopics,
    topicStatuses,
    prioritizeWeakTopics:
      strategy?.prioritizeWeakTopics,
    curriculumModel
  });


  // ------------------------------------------------
  // DENGELİ — PRACTICE DESTEKLİ NEW_TOPIC HAVUZU
  // ------------------------------------------------
  //
  // allowedNewTopicKeys verilmişse yalnız gerçekten
  // PRACTICE desteği bulunan NOT_STARTED konular
  // NEW_TOPIC olarak açılabilir.
  //
  // Set verilmezse eski davranış aynen korunur.
  // ------------------------------------------------

  if (
    allowedNewTopicKeys
      instanceof Set
  ) {

    topicPool =
      topicPool.filter(topic => {

        const topicKey =
          createSmartPlanTopicKey({
            subject,
            topic
          });


        return (
          allowedNewTopicKeys
            .has(topicKey)
        );
      });
  }
}

       else {

      const practicePool =
  getPracticeTopicPool({
    subject,
    selectedTopics,
    topicStatuses,
    prioritizeWeakTopics:
      strategy?.prioritizeWeakTopics,
    curriculumModel
  });


      topicPool =
        practicePool.filter(topic => {

          const status =
            getSmartPlanTopicStatus({
              subject,
              topic,
              topicStatuses
            });


          // Öğrenci bu konuyu daha önce görmüşse
          // Practice / Review doğrudan mümkündür.
          if (
            status !==
            SMART_PLAN_TOPIC_STATUS.NOT_STARTED
          ) {
            return true;
          }


          // NOT_STARTED bir konu ancak aynı plan
          // içerisinde NEW_TOPIC olarak gerçekten
          // açılacaksa Practice / Review havuzuna
          // girebilir.
          if (
            !(
              allowedNotStartedTopicKeys
              instanceof Set
            )
          ) {
            return false;
          }


          const topicKey =
            createSmartPlanTopicKey({
              subject,
              topic
            });


          return (
            allowedNotStartedTopicKeys
              .has(topicKey)
          );
        });
    }



// ----------------------------------------------------
// İLK PRACTICE GARANTİSİ AKTİFSE
// ZORUNLU KONULAR ÖNCE ÜRETİLSİN
// ----------------------------------------------------

if (
  firstPracticeGuaranteeActive &&

  type ===
    SMART_PLAN_TASK_TYPES.PRACTICE
) {

  const mandatoryTopics = [];

  const optionalTopics = [];


  topicPool.forEach(
    topic => {

      const status =
        getSmartPlanTopicStatus({
          subject,
          topic,
          topicStatuses
        });


      const topicKey =
        createSmartPlanTopicKey({
          subject,
          topic
        });


      const isMandatory =
        status ===
          SMART_PLAN_TOPIC_STATUS
            .NOT_STARTED &&

        allowedNotStartedTopicKeys
          .has(topicKey);


                 if (
        isMandatory
      ) {
        mandatoryTopics.push(
          topic
        );
      }

      else {
        optionalTopics.push(
          topic
        );
      }
    }
  );


  topicPool = [
    ...mandatoryTopics,
    ...optionalTopics
  ];


  mandatoryFirstPracticeMinutesForSubject =
    mandatoryTopics.length *
    SMART_PLAN_SLOT_DEFAULTS
      .durations.practice;
}


 if (
  topicPool.length === 0
) {
  return;
}


// ----------------------------------------------------
// ZORUNLU NEW_TOPIC KAPSAMASI
// ----------------------------------------------------
//
// Üst katman, tüm NEW_TOPIC kapsamının mevcut
// NEW_TOPIC bütçesine sığdığını doğrulamışsa:
//
// ders önceliği / ağırlık dağılımı yüzünden
// herhangi bir dersin konuları sessizce düşürülemez.
//
// Her konu için tam bir NEW_TOPIC slotu ayrılır.
//
// Diğer tüm durumlarda eski ağırlıklı bütçe
// davranışı aynen korunur.
// ----------------------------------------------------

const subjectBudget =
  (
    firstPracticeGuaranteeActive &&

    type ===
      SMART_PLAN_TASK_TYPES.PRACTICE
  )
    ? (
        mandatoryFirstPracticeMinutesForSubject +
        weightedSubjectBudget
      )

    : (
        guaranteeFullNewTopicCoverage &&

        type ===
          SMART_PLAN_TASK_TYPES.NEW_TOPIC
      )
        ? (
            topicPool.length *
            SMART_PLAN_SLOT_DEFAULTS
              .durations.newTopic
          )

        : weightedSubjectBudget;


if (
  subjectBudget <
  SMART_PLAN_SLOT_DEFAULTS
    .minSlotMinutes
) {
  return;
}


let remaining =
  subjectBudget;

    let topicIndex = 0;


  // ----------------------------------------------------
// ÖNCELİK DUYARLI PRACTICE / REVIEW TEKRAR SINIRI
// ----------------------------------------------------
//
// Küçük konu havuzlarında ders önceliğinin
// ağırlıklı bütçede hesaplanıp tekrar tavanında
// kaybolmasını engeller.
//
// NORMAL:
//
// plan haftası başına konu başına yaklaşık
// 1 tekrar kapasitesi.
//
// PRIORITY:
//
// 1.5 katsayı.
//
// VERY_HIGH:
//
// 2 katsayı.
//
// Örn. 1 haftalık tek konu:
//
// NORMAL    → max 1
// PRIORITY  → max 2
// VERY_HIGH → max 2
//
// Örn. 4 haftalık tek konu:
//
// NORMAL    → max 4
// PRIORITY  → max 6
// VERY_HIGH → max 8
//
// Bu değer bir HEDEF değildir.
//
// Gerçek görev sayısını yine:
//
// - dersin weightedSubjectBudget'i,
// - görev süresi,
// - minimum slot süresi,
// - Scheduler yerleştirme kuralları
//
// sınırlar.
//
// Yani VERY_HIGH olmak otomatik olarak
// 8 tekrar üretmez; yalnız öncelik bütçesinin
// gerektiğinde gerçek göreve dönüşebilmesine
// izin verir.
// ----------------------------------------------------

const baseRepeatLimitPerTopic =
  Math.max(
    1,
    Math.floor(
      Number(
        weeks || 1
      )
    )
  );


const isQuickSingleWeekPlan =
  Number(
    weeks || 1
  ) === 1;


const repeatLimitPerTopic =
  isQuickSingleWeekPlan
    ? Math.max(
        1,

        Math.ceil(
          baseRepeatLimitPerTopic *
          Math.max(
            1,
            Number(
              weight || 1
            )
          )
        )
      )
    : baseRepeatLimitPerTopic;

       // ----------------------------------------------------
    // BAŞLANGIÇ / STRATEJİ BAZLI YENİ KONU HIZI
    // ----------------------------------------------------
    //
    // Strateji pacing tanımlamıyorsa eski davranış korunur.
    //
    // Örn:
    // Başlangıç + TYT Matematik + 4 hafta
    // → haftada 1
    // → plan boyunca en fazla 4 NEW_TOPIC
    // ----------------------------------------------------

    const configuredNewTopicsPerWeek =
      Number(
        strategy
          ?.newTopicPacing
          ?.subjectOverrides
          ?.[subject] ??
        strategy
          ?.newTopicPacing
          ?.defaultPerWeek ??
        0
      );


 const newTopicPlanLimit =
  configuredNewTopicsPerWeek > 0
    ? Math.max(
        1,
        Math.floor(
          Number(weeks || 1) *
          configuredNewTopicsPerWeek
        )
      )
    : topicPool.length;


const maxNewTopicIterations =
  Math.min(
    topicPool.length,
    newTopicPlanLimit
  );


    const maxTopicIterations =
      type ===
        SMART_PLAN_TASK_TYPES.NEW_TOPIC
        ? maxNewTopicIterations
        : topicPool.length *
          repeatLimitPerTopic;


    while (
      remaining >=
      SMART_PLAN_SLOT_DEFAULTS
        .minSlotMinutes
    ) {

      if (
        topicIndex >=
        maxTopicIterations
      ) {
        break;
      }


                  const topic =
        type ===
          SMART_PLAN_TASK_TYPES.NEW_TOPIC
          ? topicPool[topicIndex]
          : topicPool[
              topicIndex %
              topicPool.length
            ];


      let preferredDuration;


      if (
        type ===
        SMART_PLAN_TASK_TYPES.NEW_TOPIC
      ) {
        preferredDuration =
          SMART_PLAN_SLOT_DEFAULTS
            .durations.newTopic;
      }

      else if (
        type ===
        SMART_PLAN_TASK_TYPES.REVIEW
      ) {
        preferredDuration =
          SMART_PLAN_SLOT_DEFAULTS
            .durations.review;
      }

      else {
        preferredDuration =
          SMART_PLAN_SLOT_DEFAULTS
            .durations.practice;
      }


      const duration =
        resolveSlotDuration({
          preferredDuration,
          remainingMinutes:
            remaining
        });


      if (!duration) {
        break;
      }


                  const topicStatus =
        getSmartPlanTopicStatus({
          subject,
          topic,
          topicStatuses
        });


            const resource =
        getSmartPlanResourceSuggestion({
          subject,
          difficulty,

          preferredResources:
            resourcePreferences?.[
              subject
            ]
        });


      slots.push(
        createStudySlot({
          index: slotIndex,

          type,

          subject,

          topic,

          duration,

          difficulty,

          resource,

          questionCount:
            getQuestionCount({
              difficulty,
              type
            }),

          priority,

          priorityWeight:
            weight,

                   topicStatus,

          reason:
            strategy
              ?.prioritizeWeakTopics &&
            topicStatus ===
              SMART_PLAN_TOPIC_STATUS
                .INCOMPLETE
              ? 'weak_topic'
              : (
                  priority !==
                  SMART_PLAN_PRIORITY_LEVELS.NORMAL
                    ? 'priority_subject'
                    : 'strategy_distribution'
                ),

          // --------------------------------------------
          // ÖĞRENME BİRİMİ METADATA
          // --------------------------------------------
          //
          // Bu alanlar şu aşamada planlama davranışını
          // DEĞİŞTİRMEZ.
          //
          // Amaç:
          // Aynı konuya ait NEW_TOPIC / PRACTICE /
          // REVIEW görevlerini gelecekte güvenli biçimde
          // birbirine bağlayabilmek.
          //
          // Plan daha sonra düzenlense, taşınsa veya
          // kısmen yeniden üretilse bile konu bağı
          // korunabilir.
          // --------------------------------------------

                             metadata: {

            learningUnitId:
              createSmartPlanTopicKey({
                subject,
                topic
              }),

            learningStage:
              type ===
                SMART_PLAN_TASK_TYPES.NEW_TOPIC
                ? 'introduction'
                : (
                    type ===
                      SMART_PLAN_TASK_TYPES.PRACTICE
                      ? 'practice'
                      : (
                          type ===
                            SMART_PLAN_TASK_TYPES.REVIEW
                            ? 'review'
                            : null
                        )
                  ),

            // NOT_STARTED bir konu için bu görevin
            // hangi aşamaya bağımlı olduğunu tarif eder.
            //
            // ŞİMDİLİK yalnız metadata'dır;
            // Scheduler henüz bunu kullanmayacak.
            learningRequiresStage:
              topicStatus ===
                SMART_PLAN_TOPIC_STATUS.NOT_STARTED
                ? (
                    type ===
                      SMART_PLAN_TASK_TYPES.PRACTICE
                      ? 'introduction'
                      : (
                          type ===
                            SMART_PLAN_TASK_TYPES.REVIEW
                            ? 'practice'
                            : null
                        )
                  )
                : null,

            // Yalnız sıfırdan öğrenilen konular
            // zorunlu öğrenme zincirine tabidir.
            //
            // INCOMPLETE / COMPLETED konu çalışmaları
            // mevcut esnek davranışlarını koruyabilir.
            learningSequenceRequired:
              topicStatus ===
                SMART_PLAN_TOPIC_STATUS.NOT_STARTED &&
              (
                type ===
                  SMART_PLAN_TASK_TYPES.NEW_TOPIC ||
                type ===
                  SMART_PLAN_TASK_TYPES.PRACTICE ||
                type ===
                  SMART_PLAN_TASK_TYPES.REVIEW
              )
          }
        })
      );


            

      slotIndex += 1;

      usedMinutes +=
        duration;

      remaining -=
        duration;

      topicIndex += 1;
    }
  });


  return {
    slots,
    usedMinutes,
    nextIndex: slotIndex
  };
};


// ======================================================
// 19. DENEME SLOTU ÜRETİCİ
// ======================================================

const buildAssessmentSlots = ({
  budget,
  weeks,
  strategyId,
  difficulty,
  startingIndex,
  fullMockMinutes,
  allowBroadAssessments = true
}) => {

  const slots = [];

  let slotIndex =
    startingIndex;

  let usedMinutes = 0;

  let remaining =
    budget;


  const fullMockFrequency =
    getFullMockFrequencyWeeks(
      strategyId
    );


  const branchMockFrequency =
    getBranchMockFrequencyWeeks(
      strategyId
    );


  const requestedFullMocks =
  allowBroadAssessments &&
  fullMockFrequency > 0
    ? Math.floor(
        weeks /
        fullMockFrequency
      )
    : 0;


const requestedBranchMocks =
  allowBroadAssessments &&
  branchMockFrequency > 0
    ? Math.floor(
        weeks /
        branchMockFrequency
      )
    : 0;


  // ----------------------------------------------------
  // TAM DENEMELER
  // ----------------------------------------------------

  for (
    let i = 0;
    i < requestedFullMocks;
    i += 1
  ) {

   const preferredDuration =
  Number(fullMockMinutes) > 0
    ? Number(fullMockMinutes)
    : SMART_PLAN_SLOT_DEFAULTS
        .durations.fullMock;


// Tam deneme atomiktir.
// Bütçe yetmiyorsa kısaltılmış tam deneme üretme.
if (
  remaining <
  preferredDuration
) {
  break;
}

const duration =
  preferredDuration;


    slots.push(
      createStudySlot({
        index: slotIndex,

        type:
          SMART_PLAN_TASK_TYPES
            .FULL_MOCK,

        duration,

        difficulty,

        reason:
          'strategy_assessment',

        metadata: {
          assessmentOrder:
            i + 1
        }
      })
    );


    slotIndex += 1;

    usedMinutes +=
      duration;

    remaining -=
      duration;


    // Deneme analizini mümkünse hemen slot havuzuna ekle.
    const analysisDuration =
      resolveSlotDuration({
        preferredDuration:
          SMART_PLAN_SLOT_DEFAULTS
            .durations.mockAnalysis,

        remainingMinutes:
          remaining
      });


    if (analysisDuration) {

      slots.push(
        createStudySlot({
          index: slotIndex,

          type:
            SMART_PLAN_TASK_TYPES
              .MOCK_ANALYSIS,

          duration:
            analysisDuration,

          difficulty: null,

          reason:
            'mock_follow_up'
        })
      );


      slotIndex += 1;

      usedMinutes +=
        analysisDuration;

      remaining -=
        analysisDuration;
    }
  }


  // ----------------------------------------------------
  // BRANŞ DENEMELERİ
  // ----------------------------------------------------

  for (
    let i = 0;
    i < requestedBranchMocks;
    i += 1
  ) {

    const duration =
      resolveSlotDuration({
        preferredDuration:
          SMART_PLAN_SLOT_DEFAULTS
            .durations.branchMock,

        remainingMinutes:
          remaining
      });


    if (!duration) {
      break;
    }


    slots.push(
      createStudySlot({
        index: slotIndex,

        type:
          SMART_PLAN_TASK_TYPES
            .BRANCH_MOCK,

        duration,

        difficulty,

        questionCount:
          getQuestionCount({
            difficulty,
            type:
              SMART_PLAN_TASK_TYPES
                .BRANCH_MOCK
          }),

        reason:
          'strategy_assessment',

        metadata: {
          assessmentOrder:
            i + 1
        }
      })
    );


    slotIndex += 1;

    usedMinutes +=
      duration;

    remaining -=
      duration;
  }


  return {
    slots,
    usedMinutes,
    unusedMinutes:
      remaining,
    requestedFullMocks,
    requestedBranchMocks,
    nextIndex:
      slotIndex
  };
};


// ======================================================
// 20. BUFFER SLOTU ÜRETİCİ
// ======================================================

const buildBufferSlots = ({
  count,
  startingIndex
}) => {

  const slots = [];

  let slotIndex =
    startingIndex;


  for (
    let i = 0;
    i < count;
    i += 1
  ) {

    slots.push(
      createStudySlot({
        index: slotIndex,

        type:
          SMART_PLAN_TASK_TYPES
            .BUFFER,

        duration:
          SMART_PLAN_SLOT_DEFAULTS
            .bufferMinutes,

        reason:
          'weekly_buffer'
      })
    );


    slotIndex += 1;
  }


  return {
    slots,
    usedMinutes:
      count *
      SMART_PLAN_SLOT_DEFAULTS
        .bufferMinutes,

    nextIndex:
      slotIndex
  };
};


// ======================================================
// 21. SLOT SAYILARI
// ======================================================

const countSlotsByType = slots => {

  return slots.reduce(
    (acc, slot) => {

      acc[slot.type] =
        (acc[slot.type] || 0) + 1;

      return acc;
    },
    {}
  );
};


// ======================================================
// 22. SLOT DAKİKALARI
// ======================================================

const sumSlotMinutesByType = slots => {

  return slots.reduce(
    (acc, slot) => {

      acc[slot.type] =
        (acc[slot.type] || 0) +
        Number(slot.duration || 0);

      return acc;
    },
    {}
  );
};


// ======================================================
// 23. ANA SLOT BUILDER
// ======================================================
//
// Beklenen config örneği:
//
// {
//   weeks: 4,
//   examMode: "tyt",
//   field: null,
//   strategyId: "dengeli",
//   dailyMinutes: 120,
//   workingDays: [
//     "Pazartesi",
//     "Salı",
//     "Çarşamba",
//     "Perşembe",
//     "Cuma"
//   ],
//
//   selectedSubjects: [
//     "TYT - Matematik",
//     "TYT - Türkçe"
//   ],
//
//   subjectPriorities: {
//     "TYT - Matematik": "very_high",
//     "TYT - Türkçe": "normal"
//   },
//
//   selectedTopics: {},
//
//   topicStatuses: {},
//
//   difficulty: "Otomatik",
//   studentLevel: "Orta"
//
// }
//
// ======================================================

export const buildSmartPlanSlots = config => {

  // ----------------------------------------------------
  // 1. GİRDİ KONTROLÜ
  // ----------------------------------------------------

  const validation =
    validateSmartPlanInput(config);


  if (!validation.valid) {

    return {
      success: false,
      slots: [],
      errors:
        validation.errors,
      warnings:
        validation.warnings,
      summary: null
    };
  }


  // ----------------------------------------------------
  // 2. STRATEJİ
  // ----------------------------------------------------

  const strategy =
    getSmartPlanStrategy(
      config.strategyId
    );


  // ----------------------------------------------------
  // 3. GÜVENLİ DERS LİSTESİ
  // ----------------------------------------------------

  const selectedSubjects =
  sanitizeSelectedSubjects({
    selectedSubjects:
      config.selectedSubjects,

    examMode:
      config.examMode,

    field:
      config.field,

    curriculumModel:
      config.curriculumModel
  });



// ----------------------------------------------------
// HIZLI PLAN — GENİŞ ASSESSMENT KAPSAM KORUMASI
// ----------------------------------------------------
//
// 1 haftalık planda seçilen BÜTÜN derslerin konu
// kapsamı kullanıcı tarafından açıkça belirlenmişse:
//
// BRANCH_MOCK / FULL_MOCK
//
// seçilen konuların dışını ölçebileceği için otomatik
// olarak üretilmez.
//
// Burada konu sayısı için keyfî bir "dar kapsam"
// eşiği kullanılmaz.
//
// Karar:
// - 1 hafta
// - tüm seçili derslerde explicit topic scope
//
// üzerinden verilir.
// ----------------------------------------------------

const allSelectedSubjectsHaveExplicitTopicScope =
  selectedSubjects.length > 0 &&

  selectedSubjects.every(
    subject => {

      const explicitTopics =
        config.selectedTopics
          ?.[subject];


      return (
        Array.isArray(
          explicitTopics
        ) &&
        explicitTopics.length > 0
      );
    }
  );


const broadAssessmentsAllowed =
  !(
    Number(
      config.weeks
    ) === 1 &&

    allSelectedSubjectsHaveExplicitTopicScope
  );


  // ----------------------------------------------------
  // 4. ZORLUK
  // ----------------------------------------------------

  const difficulty =
    resolveSmartPlanDifficulty({
      requestedDifficulty:
        config.difficulty,

      studentLevel:
        config.studentLevel,

      strategy
    });


  // ----------------------------------------------------
  // 5. DERS AĞIRLIKLARI
  // ----------------------------------------------------

  const subjectWeights =
    buildSubjectWeights({
      selectedSubjects,

      subjectPriorities:
        config.subjectPriorities
    });


  // ----------------------------------------------------
  // 6. TOPLAM KAPASİTE
  // ----------------------------------------------------

  const capacity =
    calculateSmartPlanCapacity({
      weeks:
        config.weeks,

      workingDays:
        config.workingDays,

      dailyMinutes:
        config.dailyMinutes,

      strategy
    });


  // ----------------------------------------------------
  // 7. BUFFER
  // ----------------------------------------------------

  const bufferBudget =
    calculateBufferBudget({
      weeks:
        config.weeks,

      strategyId:
        config.strategyId,

      plannedCapacity:
        capacity.plannedCapacity
    });


  const usableMinutes =
    Math.max(
      0,
      capacity.plannedCapacity -
      bufferBudget.minutes
    );


  // ----------------------------------------------------
  // 8. STRATEJİ ORANLARI
  // ----------------------------------------------------

  const effectiveRatios =
    getEffectiveStrategyRatios(
      strategy
    );


  const typeBudgets =
    calculateTypeBudgets({
      usableMinutes,
      ratios:
        effectiveRatios
    });






  // ----------------------------------------------------
  // 9. SLOT ÜRETİMİ
  // ----------------------------------------------------

    let index = 1;

  const allSlots = [];


  // Bu plan içerisinde gerçekten NEW_TOPIC
  // olarak açılacak konular burada tutulur.
  //
  // Practice / Review üreticileri NOT_STARTED
  // konularda yalnız bu havuzu kullanabilir.
 const introducedTopicKeys =
  new Set();


// ----------------------------------------------------
// PLAN İÇİNDE GERÇEKTEN PRACTICE ÜRETİLEN
// NOT_STARTED KONULAR
// ----------------------------------------------------
//
// Başlangıç stratejisinde REVIEW,
// yalnız NEW_TOPIC olarak açılacak olmasıyla değil,
// aynı plan içerisinde gerçekten PRACTICE görevi
// üretilmiş olmasıyla mümkün olacak.
//
// Böylece:
// NEW_TOPIC → PRACTICE → REVIEW
// zinciri SlotBuilder seviyesinde de korunur.
// ----------------------------------------------------

const practicedNotStartedTopicKeys =
  new Set();


// ----------------------------------------------------
// DENGELİ — PRACTICE DESTEKLİ NEW_TOPIC ÖNİZLEMESİ
// ----------------------------------------------------
//
// Burada gerçek plana slot EKLENMEZ.
//
// Amaç:
// Mevcut PRACTICE bütçesinin hangi NOT_STARTED
// konulara gerçekten PRACTICE üretebildiğini görmek.
//
// Sonra Dengeli NEW_TOPIC üreticisi yalnız
// bu konuları açacaktır.
//
// Diğer stratejiler etkilenmez.
// ----------------------------------------------------

const balancedSupportedNewTopicKeys =
  new Set();


let balancedPotentialNewTopicCount =
  0;


// ----------------------------------------------------
// QUICK DENGELİ — ASSESSMENT BÜTÇESİNİ
// İLK PRACTICE'A GERİ KAZANDIR
// ----------------------------------------------------
//
// 1 haftalık explicit konu planında broad assessment:
//
// BRANCH_MOCK / FULL_MOCK
//
// bilinçli olarak kapatılmış durumda.
//
// Bu nedenle assessment'a ayrılmış fakat artık
// kullanılamayacak kapasite kaybolmasın.
//
// Önceliği:
//
// NEW_TOPIC → PRACTICE
//
// öğrenme zincirini tamamlamaya veriyoruz.
//
// Toplam plan kapasitesi ARTMAZ.
//
// Yalnız:
//
// assessment bütçesi
//        ↓
// mandatory follow-up practice
//
// olarak yeniden kullanılır.
// ----------------------------------------------------

const balancedQuickRecoveredAssessmentMinutes =
  (
    config.strategyId ===
      'dengeli' &&

    Number(
      config.weeks
    ) === 1 &&

    broadAssessmentsAllowed ===
      false
  )
    ? Number(
        typeBudgets.assessment || 0
      )
    : 0;


const balancedPracticeSupportBudget =
  Number(
    typeBudgets.practice || 0
  ) +
  balancedQuickRecoveredAssessmentMinutes;


const balancedQuickFirstPracticeGuarantee =
  config.strategyId ===
    'dengeli' &&

  Number(
    config.weeks
  ) === 1 &&

  broadAssessmentsAllowed ===
    false;


// Quick-week practice preview'nin gerçekten
// kaç dakika kullandığını ayrıca tutuyoruz.
//
// Amaç:
// assessment bütçesinin tamamını otomatik olarak
// PRACTICE'a devretmek yerine yalnız gerçek ihtiyaç
// kadarını ödünç vermek.
let balancedQuickPracticePreviewUsedMinutes =
  0;


if (
  config.strategyId ===
    'dengeli' &&
  Number(
    typeBudgets.practice || 0
  ) > 0
) {

  // ----------------------------------------------
  // Potansiyel olarak açılabilecek tüm
  // NOT_STARTED konular
  // ----------------------------------------------

  const potentialNewTopicKeys =
    new Set();


  subjectWeights.forEach(
    subjectInfo => {

      const subject =
        subjectInfo.subject;


      const potentialTopics =
  getNewTopicPool({
    subject,

    selectedTopics:
      config.selectedTopics || {},

    topicStatuses:
      config.topicStatuses || {},

    prioritizeWeakTopics:
      strategy?.prioritizeWeakTopics,

    curriculumModel:
      config.curriculumModel
  });


      potentialTopics.forEach(
        topic => {

          potentialNewTopicKeys.add(
            createSmartPlanTopicKey({
              subject,
              topic
            })
          );
        }
      );
    }
  );


balancedPotentialNewTopicCount =
  potentialNewTopicKeys.size;


  // ----------------------------------------------
  // Mevcut PRACTICE bütçesiyle bir önizleme üret
  // ----------------------------------------------

const practicePreview =
  buildSubjectStudySlots({

    type:
      SMART_PLAN_TASK_TYPES
        .PRACTICE,

    totalBudget:
      balancedPracticeSupportBudget,

    subjects:
      subjectWeights,

    selectedTopics:
      config.selectedTopics || {},

    topicStatuses:
      config.topicStatuses || {},

    difficulty,

    strategy,

    curriculumModel:
      config.curriculumModel,

    resourcePreferences:
      config.resourcePreferences || {},

    weeks:
      Number(config.weeks),

    allowedNotStartedTopicKeys:
      potentialNewTopicKeys,

    guaranteeFirstPracticeCoverage:
      balancedQuickFirstPracticeGuarantee,

    startingIndex:
      1
  });


balancedQuickPracticePreviewUsedMinutes =
  Number(
    practicePreview
      ?.usedMinutes ||
    0
  );


  // ----------------------------------------------
  // Gerçekten PRACTICE üretilen NOT_STARTED  // öğrenme birimlerini kaydet
  // ----------------------------------------------

  practicePreview.slots
    .forEach(slot => {

      if (
        slot?.type !==
          SMART_PLAN_TASK_TYPES.PRACTICE ||
        slot?.topicStatus !==
          SMART_PLAN_TOPIC_STATUS.NOT_STARTED ||
        !slot?.subject ||
        !slot?.topic
      ) {

        return;
      }


           balancedSupportedNewTopicKeys.add(
        createSmartPlanTopicKey({
          subject:
            slot.subject,

          topic:
            slot.topic
        })
      );
    });
}



// ----------------------------------------------------
// QUICK DENGELİ — ASSESSMENT'TAN GERÇEK BORÇLANMA
// ----------------------------------------------------
//
// Preview'nin kullandığı PRACTICE dakikası,
// asli practice bütçesini aşıyorsa yalnız aşan kısım
// assessment bütçesinden ödünç kabul edilir.
//
// Örnek:
//
// practice budget = 249
// preview used    = 225
//
// borrowed = 0
//
// Başka bir senaryoda:
//
// practice budget = 249
// preview used    = 300
//
// borrowed = 51
//
// Böylece assessment'ın tamamı gereksiz yere
// PRACTICE'a devredilmiş sayılmaz.
// ----------------------------------------------------

const balancedQuickAssessmentBorrowedByPracticeMinutes =
  balancedQuickFirstPracticeGuarantee
    ? Math.min(

        balancedQuickRecoveredAssessmentMinutes,

        Math.max(
          0,

          balancedQuickPracticePreviewUsedMinutes -
          Number(
            typeBudgets.practice || 0
          )
        )
      )
    : 0;


// ----------------------------------------------------
// DENGELİ — FULL COVERAGE İÇİN
// MINIMUM ÖĞRENME DÖNGÜSÜ DE DESTEKLENMELİ
// ----------------------------------------------------
//
// Pedagojik sözleşme:
//
// NOT_STARTED bir konu yalnızca:
//
// NEW_TOPIC
//      ↓
// en az 1 gerçek PRACTICE
//
// için mevcut plan bütçesinde destek varsa
// tam kapsamın parçası kabul edilir.
//
// Böylece:
//
// - konu sessizce "açılmış" sayılmaz,
// - yetim NEW_TOPIC üretilmez,
// - eksik kapsam warning olarak görünmeye devam eder,
// - solver'a sonradan zorla yeni görev eklenmez.
//
// Bu kural 1 haftalık ve çok haftalı tüm
// Dengeli planlarda aynıdır.
// ----------------------------------------------------

const balancedPracticeCoverageSatisfied =
  balancedSupportedNewTopicKeys
    .size >=
  balancedPotentialNewTopicCount;


// ----------------------------------------------------
// DENGELİ — TAM PEDAGOJİK KAPSAMA MÜMKÜN MÜ?
// ----------------------------------------------------
//
// Tam kapsam için iki koşul birlikte gerekir:
//
// 1. Tüm NEW_TOPIC dakikaları bütçeye sığmalı.
// 2. Her NOT_STARTED konu en az bir PRACTICE
//    desteğine sahip olmalı.
//
// Yalnız NEW_TOPIC kapasitesinin yeterli olması
// artık tek başına "tam uygulanabilir kapsam"
// anlamına gelmez.
// ----------------------------------------------------

const balancedFullNewTopicCoverageRequiredMinutes =
  balancedPotentialNewTopicCount *
  SMART_PLAN_SLOT_DEFAULTS
    .durations.newTopic;


const balancedFullNewTopicCoverageFitsBudget =
  config.strategyId ===
    'dengeli' &&

  balancedPotentialNewTopicCount >
    0 &&

  balancedFullNewTopicCoverageRequiredMinutes <=
    Number(
      typeBudgets.newTopic || 0
    ) &&

  balancedPracticeCoverageSatisfied;

let newTopicUsedMinutes =
  0;


  // ----------------------------------------------------
  // YENİ KONU
  // ----------------------------------------------------

  if (
    strategy.allowNewTopics !== false &&
    typeBudgets.newTopic > 0
  ) {

  const result =
  buildSubjectStudySlots({
    type:
      SMART_PLAN_TASK_TYPES
        .NEW_TOPIC,

    totalBudget:
      typeBudgets.newTopic,

    subjects:
      subjectWeights,

    selectedTopics:
      config.selectedTopics || {},

    topicStatuses:
      config.topicStatuses || {},

    difficulty,

strategy,

curriculumModel:
  config.curriculumModel,

resourcePreferences:
  config.resourcePreferences || {},

weeks:
  Number(config.weeks),

allowedNewTopicKeys:
  (
    config.strategyId ===
      'dengeli' &&
    !balancedFullNewTopicCoverageFitsBudget
  )
    ? balancedSupportedNewTopicKeys
    : null,

guaranteeFullNewTopicCoverage:
  (
    config.strategyId ===
      'dengeli' &&
    balancedFullNewTopicCoverageFitsBudget
  ),

startingIndex:
  index
  });

        result.slots.forEach(slot => {

      if (
        !slot?.subject ||
        !slot?.topic
      ) {
        return;
      }


      introducedTopicKeys.add(
        createSmartPlanTopicKey({
          subject:
            slot.subject,

          topic:
            slot.topic
        })
      );
    });

    allSlots.push(
      ...result.slots
    );


       newTopicUsedMinutes =
      Number(
        result.usedMinutes || 0
      );


    index =
      result.nextIndex;
  }


  // ----------------------------------------------------
  // BAŞLANGIÇ — KULLANILAMAYAN TÜR BÜTÇESİNİ
  // ESNEK ÇALIŞMALARA GERİ KAZANDIR
  // ----------------------------------------------------
  //
  // NEW_TOPIC pacing / konu havuzu nedeniyle,
  // ASSESSMENT ise strateji frekansı nedeniyle
  // kendi bütçesinin tamamını her zaman tüketemez.
  //
  // Başlangıç stratejisinde bu kullanılmayan kapasite:
  //
  // - yeni konu hızını artırmaz
  // - deneme frekansını yapay biçimde artırmaz
  // - PRACTICE / REVIEW arasında mevcut oranları
  //   korunarak yeniden dağıtılır.
  //
  // Böylece SlotBuilder'ın planladığı çekirdek kapasite
  // gereksiz yere Scheduler top-up katmanına bırakılmaz.
  // ----------------------------------------------------

   let rebalancedPracticeBudget =
  balancedQuickFirstPracticeGuarantee
    ? (
        Number(
          typeBudgets.practice || 0
        ) +
        balancedQuickAssessmentBorrowedByPracticeMinutes
      )
    : Number(
        typeBudgets.practice || 0
      );


     let rebalancedReviewBudget =
  Number(
    typeBudgets.review || 0
  );


if (
  config.strategyId ===
    'baslangic'
) {

    const unusedNewTopicBudget =
      Math.max(
        0,
        Number(
          typeBudgets.newTopic || 0
        ) -
        newTopicUsedMinutes
      );


   const assessmentPreview =
  buildAssessmentSlots({
    budget:
      typeBudgets.assessment,

    weeks:
      Number(config.weeks),

    strategyId:
      config.strategyId,

    difficulty,

    startingIndex:
      index,

    fullMockMinutes:
      config.fullMockMinutes,

    allowBroadAssessments:
      broadAssessmentsAllowed
  });


    const unusedAssessmentBudget =
      Math.max(
        0,
        Number(
          typeBudgets.assessment || 0
        ) -
        Number(
          assessmentPreview
            ?.usedMinutes || 0
        )
      );


    const recoverableFlexibleMinutes =
      unusedNewTopicBudget +
      unusedAssessmentBudget;


    const flexibleRecoveryRatioTotal =
      Number(
        effectiveRatios.practice || 0
      ) +
      Number(
        effectiveRatios.review || 0
      );


    if (
      recoverableFlexibleMinutes > 0 &&
      flexibleRecoveryRatioTotal > 0
    ) {

      const extraPracticeMinutes =
        Math.floor(
          recoverableFlexibleMinutes *
          (
            Number(
              effectiveRatios.practice ||
              0
            ) /
            flexibleRecoveryRatioTotal
          )
        );


      const extraReviewMinutes =
        recoverableFlexibleMinutes -
        extraPracticeMinutes;


      rebalancedPracticeBudget +=
        extraPracticeMinutes;

      rebalancedReviewBudget +=
        extraReviewMinutes;
    }
  }


  // ----------------------------------------------------
  // SORU ÇÖZÜMÜ
  // ----------------------------------------------------

    if (
    rebalancedPracticeBudget > 0
  ) {

    const result =
  buildSubjectStudySlots({
    type:
      SMART_PLAN_TASK_TYPES
        .PRACTICE,

       totalBudget:
      rebalancedPracticeBudget,

    subjects:
      subjectWeights,

    selectedTopics:
      config.selectedTopics || {},

    topicStatuses:
      config.topicStatuses || {},

    difficulty,

strategy,

curriculumModel:
  config.curriculumModel,

resourcePreferences:
  config.resourcePreferences || {},

    weeks:
      Number(config.weeks),
           allowedNotStartedTopicKeys:
  introducedTopicKeys,

guaranteeFirstPracticeCoverage:
  balancedQuickFirstPracticeGuarantee,

startingIndex:
  index
  });


result.slots.forEach(slot => {

  if (
    slot?.type !==
      SMART_PLAN_TASK_TYPES.PRACTICE ||
    slot?.topicStatus !==
      SMART_PLAN_TOPIC_STATUS.NOT_STARTED ||
    !slot?.subject ||
    !slot?.topic
  ) {
    return;
  }


  practicedNotStartedTopicKeys.add(
    createSmartPlanTopicKey({
      subject:
        slot.subject,

      topic:
        slot.topic
    })
  );
});


allSlots.push(
  ...result.slots
);


    index =
      result.nextIndex;
  }


  // ----------------------------------------------------
  // TEKRAR
  // ----------------------------------------------------

    if (
    rebalancedReviewBudget > 0
  ) {

    const result =
      buildSubjectStudySlots({
        type:
          SMART_PLAN_TASK_TYPES
            .REVIEW,

               totalBudget:
          rebalancedReviewBudget,

        subjects:
          subjectWeights,

        selectedTopics:
          config.selectedTopics || {},

        topicStatuses:
          config.topicStatuses || {},

                difficulty,

strategy,

curriculumModel:
  config.curriculumModel,

resourcePreferences:
  config.resourcePreferences || {},

                weeks:
          Number(config.weeks),
                     allowedNotStartedTopicKeys:
  config.strategyId ===
    'baslangic'
    ? practicedNotStartedTopicKeys
    : introducedTopicKeys,

        startingIndex:
          index
     });


    allSlots.push(
      ...result.slots
    );


    index =
      result.nextIndex;
  }

  // ----------------------------------------------------
  // DENEMELER / SCOPED ASSESSMENT
  // ----------------------------------------------------

  if (
    typeBudgets.assessment > 0
  ) {

    // ==================================================
    // A. GENİŞ ASSESSMENT'A İZİN VAR
    // ==================================================
    //
    // Mevcut uzun / geniş kapsam davranışını aynen koru.
    // ==================================================

    if (
      broadAssessmentsAllowed
    ) {

      const result =
        buildAssessmentSlots({
          budget:
            typeBudgets.assessment,

          weeks:
            Number(config.weeks),

          strategyId:
            config.strategyId,

          difficulty,

          startingIndex:
            index,

          fullMockMinutes:
            config.fullMockMinutes,

          allowBroadAssessments:
            true
        });


      allSlots.push(
        ...result.slots
      );


      index =
        result.nextIndex;
    }


    // ==================================================
    // B. QUICK DENGELİ — SCOPED WEEKLY SCAN
    // ==================================================
    //
    // 1 haftalık explicit konu planında:
    //
    // BRANCH_MOCK / FULL_MOCK kapalıysa,
    // değerlendirme katmanını tamamen kaybetmek yerine
    // yalnız gerçekten PRACTICE görmüş konuları ölçen
    // kısa bir WEEKLY_SCAN üret.
    //
    // ÖNEMLİ:
    //
    // Assessment bütçesinden PRACTICE'a gerçekten
    // ödünç verilmiş dakika önce düşülür.
    //
    // Böylece aynı kapasite iki kez harcanmaz.
    // ==================================================

    else if (
      balancedQuickFirstPracticeGuarantee
    ) {

      const safeScopedAssessmentBudget =
        Math.max(
          0,

          Number(
            typeBudgets.assessment || 0
          ) -

          Number(
            balancedQuickAssessmentBorrowedByPracticeMinutes ||
            0
          )
        );


      // ----------------------------------------------
      // GERÇEKTEN PRACTICE GÖRMÜŞ KONU KAPSAMI
      // ----------------------------------------------
      //
      // selectedTopics tek başına yeterli değildir.
      //
      // NOT_STARTED konu ancak gerçek PRACTICE slotu
      // üretildiyse tarama kapsamına girebilir.
      // ----------------------------------------------

      const scopedAssessmentTopics = [];


      selectedSubjects.forEach(
        subject => {

        const topics =
  getEffectiveTopicPool({
    subject,

    selectedTopics:
      config.selectedTopics || {},

    curriculumModel:
      config.curriculumModel
  });


          topics.forEach(
            topic => {

              const topicKey =
                createSmartPlanTopicKey({
                  subject,
                  topic
                });


              if (
                !practicedNotStartedTopicKeys
                  .has(
                    topicKey
                  )
              ) {
                return;
              }


              scopedAssessmentTopics.push({
                subject,
                topic,
                key:
                  topicKey
              });
            }
          );
        }
      );


           // ----------------------------------------------
      // SCOPED ASSESSMENT TÜRÜ
      // ----------------------------------------------
      //
      // Güvenli assessment kapsamı:
      //
      // 1 konu
      //   → TOPIC_SCAN
      //
      // 2+ konu
      //   → WEEKLY_SCAN
      //
      // 0 konu
      //   → assessment üretme
      //
      // Kapsam zaten yalnız gerçekten PRACTICE görmüş
      // NOT_STARTED konulardan oluşturulmuştur.
      // ----------------------------------------------

      const scopedAssessmentType =
        scopedAssessmentTopics.length === 1
          ? SMART_PLAN_TASK_TYPES
              .TOPIC_SCAN

          : scopedAssessmentTopics.length >= 2
            ? SMART_PLAN_TASK_TYPES
                .WEEKLY_SCAN

            : null;


         const scopedAssessmentTopicCount =
        scopedAssessmentTopics.length;


      const scopedAssessmentPreferredDuration =
        scopedAssessmentType ===
          SMART_PLAN_TASK_TYPES.TOPIC_SCAN
          ? SMART_PLAN_SLOT_DEFAULTS
              .durations
              .topicScan

          : scopedAssessmentType ===
              SMART_PLAN_TASK_TYPES.WEEKLY_SCAN
            ? Math.min(

                60,

                SMART_PLAN_SLOT_DEFAULTS
                  .durations
                  .weeklyScan +

                (
                  Math.max(
                    0,
                    scopedAssessmentTopicCount - 2
                  ) * 10
                )
              )

            : 0;


      const scopedAssessmentDuration =
        scopedAssessmentType
          ? resolveSlotDuration({

              preferredDuration:
                scopedAssessmentPreferredDuration,

              remainingMinutes:
                safeScopedAssessmentBudget
            })
          : 0;


      if (
        scopedAssessmentType &&
        scopedAssessmentDuration > 0
      ) {

        const isTopicScan =
          scopedAssessmentType ===
            SMART_PLAN_TASK_TYPES
              .TOPIC_SCAN;


        allSlots.push(
          createStudySlot({

            index,

            type:
              scopedAssessmentType,

            duration:
              scopedAssessmentDuration,

            difficulty,

            reason:
              isTopicScan
                ? 'scoped_topic_assessment'
                : 'scoped_weekly_assessment',

            metadata: {

              scopedAssessment:
                true,

              assessmentScope:
                isTopicScan
                  ? 'single_practiced_topic'
                  : 'practiced_topics',

              assessmentTargetWeek:
                Number(
                  config.weeks
                ),

              assessmentTopicCount:
                scopedAssessmentTopics
                  .length,

              assessmentTopics:
                scopedAssessmentTopics
                  .map(
                    item => ({
                      subject:
                        item.subject,

                      topic:
                        item.topic
                    })
                  )
            }
          })
        );


        index += 1;
      }
    }
  }

   

  // ----------------------------------------------------
  // BUFFER / TELAFİ
  // ----------------------------------------------------

  if (
    bufferBudget.count > 0
  ) {

    const result =
      buildBufferSlots({
        count:
          bufferBudget.count,

        startingIndex:
          index
      });


    allSlots.push(
      ...result.slots
    );


    index =
      result.nextIndex;
  }


  // ----------------------------------------------------
  // 10. ÖZET
  // ----------------------------------------------------

  const generatedMinutes =
    allSlots.reduce(
      (sum, slot) =>
        sum +
        Number(slot.duration || 0),
      0
    );


  const unusedMinutes =
    Math.max(
      0,
      capacity.plannedCapacity -
      generatedMinutes
    );


  const warnings = [
  ...validation.warnings
];


// ----------------------------------------------------
// DENGELİ — NOT_STARTED KONU KAPSAMA KONTROLÜ
// ----------------------------------------------------
//
// Ürün sözleşmesi:
//
// Kullanıcı özel konu seçmişse o kapsam,
// seçmemişse seçilen derslerin tüm müfredatı
// hedef kapsamdır.
//
// Yalnız NOT_STARTED konular zorunlu NEW_TOPIC
// kapsamasına dahildir.
//
// INCOMPLETE / COMPLETED konular bu hesabın
// parçası değildir.
//
// Kapasite yetersizse konu kaybı sessiz kalmaz.
// SlotBuilder yapılandırılmış warning üretir.
//
// NOT:
// minimumFeasibleWeeks burada hesaplanmaz.
// SlotBuilder kendi motorunu tekrar çağıramaz.
// Bu öneri daha sonra güvenli recommendation
// katmanında üretilecektir.
// ----------------------------------------------------

const topicCoverageTargetKeys =
  new Set();


selectedSubjects.forEach(
  subject => {

    const coverageTopics =
  getEffectiveTopicPool({
    subject,

    selectedTopics:
      config.selectedTopics || {},

    curriculumModel:
      config.curriculumModel
  });


    coverageTopics.forEach(
      topic => {

        const topicStatus =
          getSmartPlanTopicStatus({
            subject,
            topic,

            topicStatuses:
              config.topicStatuses || {}
          });


        if (
          topicStatus !==
            SMART_PLAN_TOPIC_STATUS
              .NOT_STARTED
        ) {
          return;
        }


        topicCoverageTargetKeys.add(
          createSmartPlanTopicKey({
            subject,
            topic
          })
        );
      }
    );
  }
);


const topicCoverageMissingKeys =
  [
    ...topicCoverageTargetKeys
  ]
    .filter(
      topicKey =>
        !introducedTopicKeys
          .has(
            topicKey
          )
    );


const topicCoverageTargetCount =
  topicCoverageTargetKeys.size;


const topicCoverageMissingCount =
  topicCoverageMissingKeys.length;


const topicCoverageCoveredCount =
  topicCoverageTargetCount -
  topicCoverageMissingCount;


const topicCoveragePercent =
  topicCoverageTargetCount > 0
    ? Math.round(
        (
          topicCoverageCoveredCount /
          topicCoverageTargetCount
        ) *
        100
      )
    : 100;


const topicCoverageRequiredMinutes =
  topicCoverageTargetCount *
  SMART_PLAN_SLOT_DEFAULTS
    .durations.newTopic;


const topicCoveragePlannedCapacityMinutes =
  Number(
    capacity.plannedCapacity || 0
  );


const topicCoverageNewTopicBudgetMinutes =
  Number(
    typeBudgets.newTopic || 0
  );


const topicCoverageCapacityFeasible =
  topicCoverageNewTopicBudgetMinutes >=
  topicCoverageRequiredMinutes;


const topicCoverageAdditionalMinutesNeeded =
  Math.max(
    0,

    topicCoverageRequiredMinutes -
    topicCoverageNewTopicBudgetMinutes
  );


// ----------------------------------------------------
// KAPASİTE YETERSİZLİĞİ UYARISI
// ----------------------------------------------------
//
// Şimdilik yalnız doğruladığımız Dengeli strateji
// sözleşmesinde aktiftir.
//
// Test 71:
// yeterli kapasite → %100 kapsam
//
// Test 72:
// yetersiz kapasite → yapılandırılmış warning
// ----------------------------------------------------

if (
  config.strategyId ===
    'dengeli' &&

  topicCoverageTargetCount >
    0 &&

  topicCoverageMissingCount >
    0
) {

  warnings.push({

    code:
      'TOPIC_COVERAGE_INSUFFICIENT',

    targetTopicCount:
      topicCoverageTargetCount,

    coveredTopicCount:
      topicCoverageCoveredCount,

    missingTopicCount:
      topicCoverageMissingCount,

    coveragePercent:
      topicCoveragePercent,

    missingTopics:
      topicCoverageMissingKeys,

    minimumMinutesPerTopic:
      SMART_PLAN_SLOT_DEFAULTS
        .durations.newTopic,

    requiredCoverageMinutes:
      topicCoverageRequiredMinutes,

    plannedCapacityMinutes:
  topicCoveragePlannedCapacityMinutes,

newTopicBudgetMinutes:
  topicCoverageNewTopicBudgetMinutes,

additionalMinutesNeeded:
  topicCoverageAdditionalMinutesNeeded,

capacityFeasible:
  topicCoverageCapacityFeasible
  });
}


if (
  unusedMinutes >=
  SMART_PLAN_SLOT_DEFAULTS
    .minSlotMinutes
) {

    warnings.push({
      code:
        'UNUSED_PLAN_CAPACITY',

      minutes:
        unusedMinutes
    });
  }


  if (
    generatedMinutes >
    capacity.rawCapacity
  ) {

    warnings.push({
      code:
        'GENERATED_CAPACITY_EXCEEDED',

      generatedMinutes,

      capacity:
        capacity.rawCapacity
    });
  }


  return {
    success: true,

    slots:
      allSlots,

    errors: [],

    warnings,

    summary: {

      strategyId:
        config.strategyId,

      strategyLabel:
        strategy.label,

      difficulty,

      weeks:
        Number(config.weeks),

      workingDayCount:
        config.workingDays.length,

      dailyMinutes:
        Number(config.dailyMinutes),

      selectedSubjects,

      rawCapacityMinutes:
        capacity.rawCapacity,

      plannedCapacityMinutes:
        capacity.plannedCapacity,

      generatedMinutes,

      unusedMinutes,

      bufferMinutes:
        bufferBudget.minutes,

      effectiveRatios,

      typeBudgets,

      slotCount:
        allSlots.length,

      slotCountsByType:
        countSlotsByType(
          allSlots
        ),

      slotMinutesByType:
        sumSlotMinutesByType(
          allSlots
        )
    }
  };
};